import React, { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [config, setConfig] = useState({ legacy_login_enabled: false, workos_enabled: true });
  const [legacyUser, setLegacyUser] = useState("");
  const [legacyPass, setLegacyPass] = useState("");

  useEffect(() => {
    axios.get(`${API}/auth/config`).then((r) => setConfig(r.data || {})).catch(() => {});
  }, []);

  const signInWithWorkOS = async () => {
    setErr(null); setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/workos/start`, {}, { withCredentials: true });
      const url = r.data?.authorization_url;
      if (!url) throw new Error("No authorization URL returned");
      window.location.href = url;
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Sign-in failed");
      setBusy(false);
    }
  };

  const signInLegacy = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await axios.post(`${API}/auth/login`,
        { username: legacyUser, password: legacyPass }, { withCredentials: true });
      window.location.href = "/";
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login failed");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ls-dark flex flex-col">
      <div className="px-8 py-6 flex items-center justify-between">
        <div className="text-white font-semibold text-lg tracking-tight" data-testid="brand-mark">
          HCRIS<span className="text-ls-accent">+</span>
          <span className="ml-2 text-xs text-zinc-400 font-normal">by LiquidSMARTS</span>
        </div>
        <div className="text-xs font-mono text-zinc-500">Phase 6 · WorkOS AuthKit</div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <h1 className="text-4xl sm:text-5xl font-semibold text-white tracking-tight leading-tight">
            Sign in to <span className="text-ls-accent">HCRIS+</span>
          </h1>
          <p className="mt-3 text-zinc-400 text-sm leading-relaxed">
            Healthcare facility benchmarking on CMS public data.
            Authentication via WorkOS AuthKit — SSO, magic link, or email + password.
          </p>

          <div className="mt-8 bg-white p-6 rounded-lg border border-zinc-200 space-y-4" data-testid="login-card">
            <button type="button" onClick={signInWithWorkOS} disabled={busy}
                    className="btn-brand w-full" data-testid="workos-signin-button">
              {busy ? "Redirecting to WorkOS…" : "Sign in with WorkOS"}
            </button>
            {err && (
              <div className="text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded-md p-2"
                   data-testid="login-error">{err}</div>
            )}
            <div className="text-[11px] font-mono text-zinc-500 leading-relaxed pt-2 border-t border-zinc-100">
              First time signing in? Use your work email — your account will be provisioned
              automatically on first successful login.
            </div>

            {config.legacy_login_enabled && (
              <details className="border-t border-zinc-100 pt-3" data-testid="emergency-login">
                <summary className="text-[11px] font-mono uppercase tracking-wider text-amber-700 cursor-pointer">
                  ⚠ Emergency sign-in
                </summary>
                <form onSubmit={signInLegacy} className="mt-3 space-y-2"
                      data-testid="emergency-login-form">
                  <div className="text-[11px] font-mono text-zinc-600">
                    Legacy shared-credential gate is currently <strong>enabled</strong>.
                    Use only when WorkOS is unavailable.
                  </div>
                  <input value={legacyUser} onChange={(e) => setLegacyUser(e.target.value)}
                         placeholder="username" required
                         className="w-full px-2 py-1 border border-zinc-300 rounded text-sm font-mono"
                         data-testid="emergency-username-input" />
                  <input type="password" value={legacyPass} onChange={(e) => setLegacyPass(e.target.value)}
                         placeholder="password" required
                         className="w-full px-2 py-1 border border-zinc-300 rounded text-sm font-mono"
                         data-testid="emergency-password-input" />
                  <button type="submit" disabled={busy}
                          className="w-full px-3 py-1.5 bg-amber-700 hover:bg-amber-800 disabled:opacity-50 text-white text-xs font-mono uppercase tracking-wider rounded"
                          data-testid="emergency-submit-btn">
                    Emergency sign-in
                  </button>
                </form>
              </details>
            )}
          </div>

          <div className="mt-6 text-[11px] font-mono text-zinc-500 leading-relaxed">
            The legacy shared-credential gate is disabled by default. In an emergency, set
            <code className="text-zinc-300 mx-1">LEGACY_LOGIN_ENABLED=true</code> on the backend
            to re-enable. See <code className="text-zinc-300">AUTH.md</code>.
          </div>
        </div>
      </div>

      <div className="px-8 py-5 text-[11px] font-mono text-zinc-600 border-t border-zinc-800">
        Data source: CMS HCRIS HOSP10 (Form 2552-10). Indicators carry full worksheet lineage.
      </div>
    </div>
  );
}
